import React from "react";
import { ChartData, ChartOptions } from "chart.js";
import MChart from "../../../components/MChart";
import { Alert, Box } from "@mui/material";

interface ChartAbsorptionFactorProps {
  rows: Array<{
    id: number;
    sqp_tsie_quenching?: string | number; // X
    efficiency?: string | number;
    fa_exp?: string | number; // Y
    fc_calc?: string | number;
  }>;
}

const RANGE_FACTOR = 0.1;

const ChartAbsorptionFactor: React.FC<ChartAbsorptionFactorProps> = ({
  rows = [],
}) => {
  const validRows = rows.filter(
    (row) =>
      row &&
      row.sqp_tsie_quenching !== undefined &&
      row.fa_exp !== undefined &&
      !(Number(row.sqp_tsie_quenching) === 0 && Number(row.fa_exp) === 0)
  );

  if (validRows.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <Alert severity="info">Sin datos</Alert>
      </Box>
    );
  }

  const sortedRows = [...validRows].sort(
    (a, b) => Number(a.sqp_tsie_quenching) - Number(b.sqp_tsie_quenching)
  );

  const minX = Math.min(
    ...sortedRows.map((row) => Number(row.sqp_tsie_quenching))
  );
  const maxX = Math.max(
    ...sortedRows.map((row) => Number(row.sqp_tsie_quenching))
  );

  const minY = Math.min(...sortedRows.map((row) => Number(row.fc_calc)));
  const maxY = Math.max(...sortedRows.map((row) => Number(row.fa_exp)));

  const minXAdjusted =
    Math.floor(minX * (1 - RANGE_FACTOR)) === 0
      ? -10
      : Math.floor(minX * (1 - RANGE_FACTOR));
  const maxXAdjusted = Math.ceil(maxX * (1 + RANGE_FACTOR));

  const minYAdjusted = Math.floor(minY * (1 - RANGE_FACTOR));
  const maxYAdjusted = Math.ceil(maxY * (1 + RANGE_FACTOR));

  const scatterDataPoints = sortedRows.map((row) => ({
    x: Number(row.sqp_tsie_quenching),
    y: Number(row.fa_exp),
  }));

  const regressionLine = sortedRows.map((row) => ({
    x: Number(row.sqp_tsie_quenching),
    y: Number(row.fc_calc),
  }));

  const lineData: ChartData<"scatter"> = {
    datasets: [
      {
        label: "FA",
        data: scatterDataPoints,
        backgroundColor: "rgb(75, 192, 192)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 2,
        pointRadius: 5,
        showLine: false,
      },
      {
        label: "",
        data: regressionLine,
        borderColor: "rgba(153, 102, 255, 1)",
        borderDash: [5, 5],
        fill: false,
        showLine: true,
        pointRadius: 0,
      },
    ],
  };

  const lineOptions: ChartOptions<"scatter"> = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "FA",
      },
    },
    scales: {
      x: {
        type: "linear",
        title: {
          display: true,
          text: "",
        },
        min: minXAdjusted,
        max: maxXAdjusted,
        ticks: {
          callback: function (val) {
            return Number(val).toFixed(0);
          },
        },
      },
      y: {
        title: {
          display: true,
          text: "",
        },
        min: minYAdjusted,
        max: maxYAdjusted,
      },
    },
  };

  return (
    <div>
      <MChart type="scatter" data={lineData} options={lineOptions} />
    </div>
  );
};

export default ChartAbsorptionFactor;
